import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { newsList } from "@/mocks/news";
import { getMediaById, getRecommendations, typeLabelMap } from "@/utils/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites, useViewHistory } from "@/hooks/useLocalLibrary";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/feature/SectionHeading";
import ShareModal from "@/pages/detail/components/ShareModal";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getMediaById(mediaList, id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addVisit } = useViewHistory();
  const [shareOpen, setShareOpen] = useState(false);
  const [tip, setTip] = useState("");

  useDocumentMeta(
    item
      ? `${item.title} - 日韩影视详情 | 日韩影视大全`
      : "作品未找到 - 日韩影视大全",
    item
      ? `${item.title}（${item.originalTitle}）是${item.year}年${item.region}${typeLabelMap[item.type]}，由${item.director}执导，${item.cast.slice(0, 2).join("、")}主演。${item.synopsis.slice(0, 60)}`
      : "该影视作品可能已下架或链接有误，可以返回首页继续浏览日韩电影、电视剧与综艺。",
    item ? `${item.title},${item.originalTitle},日韩影视详情,${item.genres.join(",")}` : "日韩影视大全",
  );

  useEffect(() => {
    if (item) addVisit(item.id);
  }, [item, addVisit]);

  useEffect(() => {
    if (!tip) return;
    const timer = window.setTimeout(() => setTip(""), 2400);
    return () => window.clearTimeout(timer);
  }, [tip]);

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这部作品"
          description="页面链接可能已经失效，或者作品信息已调整。你可以回到首页，或按分类继续浏览。"
        >
          <Link
            to="/"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
          >
            返回首页
          </Link>
          <Link
            to="/category/all"
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            查看全部作品
          </Link>
        </EmptyState>
      </div>
    );
  }

  const recommendations = getRecommendations(mediaList, item, 4);
  const fav = isFavorite(item.id);
  const relatedNews = newsList
    .filter((news) => news.title.includes(item.title) || news.tags.includes(item.title) || news.excerpt.includes(item.title))
    .slice(0, 2);
  const fallbackNews = relatedNews.length > 0 ? relatedNews : newsList.slice(0, 2);
  const metaRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join(" / ") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "片长", value: item.duration },
    { label: "集数", value: item.episodes },
    { label: "年份", value: `${item.year} 年` },
    { label: "地区", value: item.region },
    { label: "状态", value: item.status },
  ];

  const handleFavorite = () => {
    const added = toggleFavorite(item.id);
    setTip(added ? "已加入收藏，刷新页面后仍会保留" : "已取消收藏");
  };

  return (
    <div className="w-full pb-16">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={item.backdrop || item.cover}
          alt={`${item.title} 背景剧照`}
          title={`${item.title} - 日韩影视详情`}
          className="absolute inset-0 h-full w-full scale-105 object-cover object-top opacity-25 blur-[1px]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/92 to-primary-950/65" />

        <div className="relative mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-background-300">
            <Link to="/" className="cursor-pointer transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-background-400" />
            <Link
              to={`/category/${item.type}`}
              className="cursor-pointer transition-colors hover:text-accent-300"
            >
              {typeLabelMap[item.type]}
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-background-400" />
            <span className="text-background-100">{item.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[200px_minmax(0,1fr)] lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">
            <div className="mx-auto w-full max-w-[220px] md:mx-0 md:max-w-none">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-50/15 bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} 封面`}
                  title={`${item.title} - 日韩影视封面`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md border border-background-50/15 bg-background-50/5 px-3 py-2">
                <span className="flex items-center gap-1 font-label text-lg font-semibold text-accent-300">
                  <i className="ri-star-fill text-sm" />
                  {item.rating.toFixed(1)}
                </span>
                <span className="text-[11px] text-background-300">综合评分</span>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-primary-950">
                  {typeLabelMap[item.type]}
                </span>
                <span className="rounded-full border border-background-50/25 px-2.5 py-1 text-[11px] text-background-100">
                  {item.region}
                </span>
                <span className="rounded-full border border-background-50/25 px-2.5 py-1 text-[11px] text-background-100">
                  {item.year}
                </span>
                <span className="rounded-full border border-background-50/25 px-2.5 py-1 text-[11px] text-background-100">
                  {item.status}
                </span>
              </div>

              <h1 className="mt-3 font-heading text-3xl font-semibold leading-tight text-background-50 md:text-4xl lg:text-[44px]">
                {item.title}
              </h1>
              <p className="mt-1.5 font-label text-xs uppercase tracking-[0.18em] text-background-300">
                {item.originalTitle}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-background-200">「{item.tagline}」</p>

              <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2.5 text-sm sm:grid-cols-2 xl:grid-cols-3">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-2">
                    <dt className="shrink-0 text-background-400">{row.label}</dt>
                    <dd className="min-w-0 truncate text-background-100">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                    fav
                      ? "bg-background-50 text-primary-900 hover:bg-background-100"
                      : "bg-accent-500 text-primary-950 hover:bg-accent-400"
                  }`}
                >
                  <i className={`${fav ? "ri-heart-fill" : "ri-heart-line"} text-base`} />
                  {fav ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
                >
                  <i className="ri-share-line text-base" />
                  分享
                </button>
                <Link
                  to={`/category/${item.type}`}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/20 px-5 py-2.5 text-sm text-background-200 transition-colors hover:border-background-50/40 hover:text-background-50"
                >
                  返回{typeLabelMap[item.type]}列表
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>

              {tip && (
                <p className="mt-3 flex items-center gap-1.5 text-[11px] text-accent-200">
                  <i className="ri-information-line text-sm" />
                  {tip}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-8">
          <div className="flex flex-col gap-4">
            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                  <i className="ri-file-text-line text-base" />
                </span>
                <h2 className="font-heading text-base font-semibold text-foreground-950">剧情简介</h2>
              </div>
              <p className="mt-4 text-sm leading-[1.9] text-foreground-700">{item.synopsis}</p>
              <p className="mt-3 text-sm leading-[1.9] text-foreground-500">
                本页面为演示数据，作品、人物与情节均为虚构内容，用于展示影视资料与浏览体验。
              </p>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-award-line text-base" />
                </span>
                <h2 className="font-heading text-base font-semibold text-foreground-950">作品亮点</h2>
              </div>
              <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {item.highlights.map((highlight) => (
                  <li
                    key={highlight}
                    className="flex items-start gap-2 rounded-md bg-background-100/70 px-3 py-2.5 text-xs leading-relaxed text-foreground-700"
                  >
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent-500 text-primary-950">
                      <i className="ri-check-line text-[10px]" />
                    </span>
                    {highlight}
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-team-line text-base" />
                </span>
                <h2 className="font-heading text-base font-semibold text-foreground-950">演职人员</h2>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-md border border-background-200/70 px-3 py-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-950 font-heading text-xs text-background-50">
                    {item.director.slice(0, 1)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground-900">{item.director}</p>
                    <p className="text-[11px] text-foreground-500">导演</p>
                  </div>
                </div>
                {item.cast.map((actor, index) => (
                  <div
                    key={actor}
                    className="flex items-center gap-3 rounded-md border border-background-200/70 px-3 py-2.5"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-xs ${
                        index === 0 ? "bg-accent-100 text-accent-900" : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {actor.slice(0, 1)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground-900">{actor}</p>
                      <p className="text-[11px] text-foreground-500">主演</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-4">
            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">分类</h2>
              <p className="mt-2 font-heading text-base font-semibold text-foreground-950">所属分类</p>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to={`/category/${item.type}`}
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-folder-line text-base text-primary-600" />
                    日韩{typeLabelMap[item.type]}
                  </span>
                  <i className="ri-arrow-right-s-line text-base" />
                </Link>
                <Link
                  to={`/category/all?genre=${encodeURIComponent(item.genres[0])}`}
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-base text-primary-600" />
                    {item.genres[0]}题材
                  </span>
                  <i className="ri-arrow-right-s-line text-base" />
                </Link>
              </div>
              <div className="mt-4 border-t border-background-200/70 pt-3">
                <div className="flex items-center justify-between text-[11px] text-foreground-500">
                  <span>站内热度</span>
                  <span className="font-label text-foreground-800">{item.heat}</span>
                </div>
                <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-background-200">
                  <div className="h-full rounded-full bg-accent-500" style={{ width: `${item.heat}%` }} />
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-foreground-950">相关资讯</h2>
                <Link to="/news" className="cursor-pointer text-[11px] text-foreground-500 hover:text-primary-700">
                  更多
                </Link>
              </div>
              <div className="mt-3 flex flex-col gap-3">
                {fallbackNews.map((news) => (
                  <NewsCard key={news.id} item={news} variant="compact" />
                ))}
              </div>
            </section>

            <section className="rounded-lg bg-secondary-50 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-bookmark-line text-base" />
                </span>
                <h2 className="font-heading text-sm font-semibold text-secondary-950">我的收藏</h2>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-secondary-900/80">
                收藏状态保存在当前浏览器，刷新页面后依然保留。你可以随时回来查看这部作品。
              </p>
              <p className="mt-3 text-[11px] text-secondary-700">
                当前状态：{fav ? "已收藏本作品" : "尚未收藏本作品"}
              </p>
            </section>
          </aside>
        </div>

        <section className="mt-10 border-t border-background-200/80 pt-8 md:mt-12">
          <SectionHeading
            eyebrow="You May Also Like"
            title="相关推荐"
            description={`根据类型、地区与题材相关性，为《${item.title}》推荐的同类作品。`}
            actionLabel={`更多${typeLabelMap[item.type]}`}
            actionTo={`/category/${item.type}`}
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
            {recommendations.map((recommend) => (
              <MediaCard key={recommend.id} item={recommend} />
            ))}
          </div>
        </section>
      </div>

      {shareOpen && <ShareModal title={item.title} onClose={() => setShareOpen(false)} />}
    </div>
  );
}